/* Dynamic Plot code copyright 2024 (c) Tuva Labs Inc.
 *
 * created by cmiller
 */
import ReactDOM from 'react-dom';
import BigCardView, {BigCardOutline} from '../../components/elem-play/BigCardView';
import ElemPlayContainer from "./ElemPlayContainer";
import { ElemCard } from "./ElemCard";

export class BigCard {
    constructor( outline=null, htmlRoot=null ) {
        this.outline = outline; // svg for visible outline of big card, or null
        this.htmlRoot = htmlRoot;
        this.caseIndices = []; // case indices of cards dropped (and visible in Big card)
    }

    static border = 50;
    static corner = 16;
    static width = 260; // match bigCardWidth in BigCardView.js
    static height = 310; // match bigCardHeight in BigCardView.js

    initVisuals( bigCardIndex, rect, paper ) {
        const outline = paper.foreignObject( '', rect.x, rect.y, rect.width, rect.height )
            .data( "index", bigCardIndex );
        const body = outline.node.firstChild;
        body.style.height = '100%';
        const container = document.createElement( 'div' );
        container.classList.add( 'tde-fill' );
        body.appendChild( container );
        // Render using react
        const root = ReactDOM.createRoot( container );
        root.render( <BigCardOutline Dyn={ this.Dyn }/> );
        this.outline = outline;
        this.htmlRoot = root;
    }

    /** is the Big card visible (not hidden) */
    cardIsVisible( Dyn ) {
        return Dyn.plotUtil.isElementVisible( this.outline );
    }
    /** is the given case in the big card (dropped and not yet removed/reset) */
    hasCaseIndex( caseIndex ) {
        return this.caseIndices.includes( caseIndex );
    }
    /** does the card have any cases visible? */
    cardHasCases() {
        return this.caseIndices.length > 0;
    }
    addCase( caseIndex ) {
        this.caseIndices.push( caseIndex );
    }
    removeCase() {
        return this.caseIndices.shift();
    }

    getBBox( Dyn ) {
        return new Dyn.mathUtil.Rect( this.outline.getBBox());
    }
}

export class ElemBigView {

    static numBigCardsInPlot = 1;

    constructor( Dyn, container ) {

        // references to other objects
        this.Dyn = Dyn;
        this.container = container;

        // our stuff
        this.bounds = new Dyn.mathUtil.Rect(0,0,0,0); // area where big cards are allowed
        this.inPlotBounds = new Dyn.mathUtil.Rect(0,0,0,0); // area where big cards open when plotting
        this.bigCards = [ new BigCard(), new BigCard() ];
    }

    destroy() {
        this.Dyn = null;
        this.container = null;
    }

    initVisuals() {
        //let Dyn = this.Dyn;

        // this.topMargin.height = Dyn.plotUtil.kPlotTitleFontSize * 2;
        // this.topMargin.title = this.container.paper.text( 0, 0, Dyn.loc(ElemPlayView.playTitle) )
        //     .attr({ 'font-size': Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize )});
        //
        // this.bottomMargin.height = Dyn.plotUtil.kAxisTitleFontSize * 2;
        // this.bottomMargin.title = this.container.paper.text( 0, 0, "" )
        //     .attr({ 'font-size': Dyn.preferences.getFontSize( Dyn.plotUtil.kAxisTitleFontSize )})
        //     .hide();
    }

    updateVisuals() {
        this.bigCards.forEach((bigCard, bigCardIndex) => {
            this._updateOneCard( bigCard, bigCardIndex );
        });
    }

    _updateOneCard( bigCard, bigCardIndex ) {
        const isEditing = this.container.isEditing;
        const hasPlot = this.container.hasPlot();
        if( bigCard.cardHasCases() ) {
            let caseIndex = bigCard.caseIndices[bigCard.caseIndices.length - 1],
                smallCardIndex = this.container.home.caseIndexToSmallCardIndex( caseIndex );
            bigCard.htmlRoot.render( <BigCardView onClose={ () => this.removeAnimateCardsFromBigCard( bigCardIndex, false ) }
                                                  onEdit={ () => {
                                                    this.container.setEditModeOn(caseIndex);
                                                  }}
                                                  editable={ this.container.isEditable }
                                                  onKeyPressed={(e) => this.openCardkeyDownHandler(e, bigCardIndex)}
                                                  Dyn={ this.Dyn } caseIndex={ caseIndex } smallCardIndex={ smallCardIndex }/> );
        } else {
            bigCard.htmlRoot.render( <BigCardOutline Dyn={ this.Dyn } hideDragDrop={ hasPlot } /> );
        }
        if( bigCard?.outline ) {
            const showOutline = (!isEditing) &&
                (ElemPlayContainer.bigCardsOnLeft || (!hasPlot) || (bigCardIndex < ElemBigView.numBigCardsInPlot));
            this.Dyn.plotUtil.showOrHideElement( bigCard.outline, showOutline );
        }
    }

    resize( newSize, inPlotBounds ) {
        let Dyn = this.Dyn;
        this.bounds.copyRect( newSize );
        this.bounds.addDebugRect( this.container.paper, "red" );
        Dyn.assertValidRect( this.bounds );
        this.inPlotBounds.copyRect( inPlotBounds );
        this.arrangeBigCards( this.container.hasPlot());
    }

    /** arrange small cards in z-order, card backs over outlines */
    layerElements() {
        this.bigCards.forEach((bigCard) => {
            bigCard.outline.toFront();
        });
    }

    /** assuming cards are not in a plot, are any cards dropped on the big card? */
    getBigCardState() {
        let casesOfEachCard = this.getBigCardCases();
        if( casesOfEachCard.flat().length > 0 ) {
            return { plot:"bigCards", cases:casesOfEachCard };
        }
        return this.container.home.getHomeState();
    }

    getBigCardCases() {
        return this.bigCards.map((bc) => [...bc.caseIndices]);
    }

    _getBigCardScale() {   // get scale of cards that fits in view
        let borderX = this.bounds.width / 10,
            borderY = this.bounds.height / 10,
            maxCardWidth1Across = (this.bounds.width - (2 * borderX)) / 1,
            maxCardWidth2Across = (this.bounds.width - (3 * borderX)) / 2,
            maxCardHeight1Down = (this.bounds.height - (2 * borderY)) / 1,
            maxCardHeight2Down = (this.bounds.height - (3 * borderX)) / 2,
            scale2Across = Math.min( maxCardWidth2Across / BigCard.width, maxCardHeight1Down / BigCard.height ),
            scale2Down = Math.min( maxCardWidth1Across / BigCard.width, maxCardHeight2Down / BigCard.height ),
            bestFitIsAcross = ( scale2Across >= scale2Down ),
            bestScale = Math.min( 1.0, bestFitIsAcross ? scale2Across : scale2Down );
        return {
            is2Across: bestFitIsAcross,
            scale: bestScale,
            width: BigCard.width * bestScale,
            height: BigCard.height * bestScale,
            corner: BigCard.corner,
            borderX: borderX,
            borderY: borderY
        };
    }

    /** create or update the position of the two big card areas */
    arrangeBigCards( hasPlot = false ) {
        let Dyn = this.Dyn,
            fit = this._getBigCardScale(),
            moveCardToLeftOfPlot = hasPlot && !ElemPlayContainer.bigCardsOnLeft;

        // add or reposition card 0
        let bigCardIndex = 0,
            bigCard = this.bigCards[0],
            bcRect = new Dyn.mathUtil.Rect(
                this.bounds.centerX() - (fit.is2Across ? (fit.width + fit.borderX / 2) : (fit.width / 2)),
                this.bounds.centerY() - (fit.is2Across ? (fit.height / 2) : (fit.height + fit.borderY / 2)),
                fit.width, fit.height );
        if( !bigCard.outline ) {
            bigCard.initVisuals( bigCardIndex, bcRect, this.container.paper );
        }
        if( moveCardToLeftOfPlot ) {
            // arrange/position one big card to be visible over the home view, when opening a card with OrderBy or GroupBy
            Dyn.assert( this.container.hasPlot() );
            const border = 10, // white space around big card on all sides
                leftOffset = 15, // but shift left to better center in Cards area
                ipb = this.inPlotBounds,
                width = Math.max( 0, ipb.width - 2 * border ),
                height = BigCard.height / BigCard.width * width;
            bcRect = new Dyn.mathUtil.Rect(
                ipb.left() + leftOffset, Math.max( 0, ipb.bottom() - height ), width, height );
        }
        this.moveBigCardOutline( bigCard.outline, bcRect );
        this._updateOneCard( bigCard, bigCardIndex );

        // add or reposition card 1
        bigCardIndex = 1;
        bigCard = this.bigCards[bigCardIndex];

        if( moveCardToLeftOfPlot && bigCardIndex < ElemBigView.numBigCardsInPlot ) {
            Dyn.assert( false, "card 2 must be moved to plotting position");
        } else {
            if( fit.is2Across ) {
                bcRect.x += fit.width + fit.borderX;
            } else {
                bcRect.y += fit.height + fit.borderY;
            }
        }
        if( ! bigCard.outline ) {
            bigCard.initVisuals( bigCardIndex, bcRect, this.container.paper );
        }
        this.moveBigCardOutline( bigCard.outline, bcRect );
        this._updateOneCard( bigCard, bigCardIndex );
    }

    /** add or update a Big Card Case View */
    addBigCardCaseView(bigCardIndex, caseIndex) {
        let Dyn = this.Dyn,
            bigCard = this.bigCards[bigCardIndex],
            smallCardIndex = this.container.home.caseIndexToSmallCardIndex( caseIndex );
        Dyn.assertArrayIndex( this.bigCards, bigCardIndex );
        Dyn.assert( bigCard.caseIndices.includes( caseIndex ), "case should have been added at start of animation", caseIndex );
        bigCard.htmlRoot.render(<BigCardView onClose={() => this.removeAnimateCardsFromBigCard(bigCardIndex, false )}
                                             onEdit={ () => {
                                                this.container.setEditModeOn(caseIndex);
                                             }}
                                             editable={ this.container.isEditable }
                                             onKeyPressed={(e) => this.openCardkeyDownHandler(e, bigCardIndex)}
                                             Dyn={this.Dyn} caseIndex={caseIndex} smallCardIndex={ smallCardIndex }/>);
    }

    /** handle key down when big card is open (not outline), and card border has key focus */
    openCardkeyDownHandler( event, bigCardIndex ) {
        switch( event.key ) {
            case "Escape":
            case "ArrowLeft":
                this.removeAnimateCardsFromBigCard( bigCardIndex );
                break;
        }
    }

    moveBigCardOutline( outline, bcRect ) {
        outline.attr({ x:bcRect.x, y:bcRect.y, width:bcRect.width, height:bcRect.height });
        return outline; // for chaining
    }

    highlightBigCard( onOff, bigCardIndex, caseIndex ) {
        this.Dyn.assertValidArray( this.bigCards, bigCardIndex );
        let bigCard = this.bigCards[bigCardIndex];
        if( ! bigCard.cardHasCases()) {
            // Highlight only if the big card is empty
            const hasPlot = this.container.hasPlot();
            bigCard.htmlRoot.render(<BigCardOutline Dyn={this.Dyn} highlight={onOff} hideDragDrop={ hasPlot } />);
        }
    }

    /** remove and animate home any previously dropped cases on this big card */
    dropRemovePreviousCases( bigCard, bigCardIndex ) {
        const hasPlot = this.container.hasPlot();
        let anyRemoved = false;
        while( bigCard.cardHasCases()) {
            // return previous cases, if any, to home
            let removedCaseIndex = bigCard.removeCase();
            //console.log( "drop remove previous case", removedCaseIndex );
            let smallCardIndex = this.container.home.caseIndexToSmallCardIndex( removedCaseIndex );
            if( smallCardIndex >= 0 ) { // make sure it was not filtered out
                this.hideCardUnderBigCard( smallCardIndex, bigCardIndex );
                this.container.home.animateCardToHome( smallCardIndex, this.container.fastAnimation(), hasPlot );
            }
            anyRemoved = true;
        }
        return anyRemoved;
    }

    /**
     * handle start of drop animation
     * NOTE: because animation is slow, multiple cases can be dropped while the
     * first case animation has not completed; here we add and remove cases in a queue for each card,
     * so cases are added and moved in, or removed and moved out, in queue order.
     */
    dropAnimationStart( dragResult ) {
        let Dyn = this.Dyn,
            bigCard = this.bigCards[dragResult.bigCardIndex],
            undoData = this.container.save();
        Dyn.assertArrayIndex( this.bigCards, dragResult.bigCardIndex );

        // remove previous cases from big card, if any
        if( this.dropRemovePreviousCases( bigCard, dragResult.bigCardIndex )) {
            this._updateOneCard( bigCard, dragResult.bigCardIndex );
        }
        // add the new case
        bigCard.addCase( dragResult.caseIndex );
        if( ! this.container.hasPlot() ) {
            this.container.setState( this.getBigCardState());
            this.container.undoer.saveUndoOfState( 'dropCard', undoData, Dyn.loc( "Drop Card" ) );
        }
    }

    /** handle end of animation after drop of small card onto big card */
    dropAnimationEnd( dragResult ) {
        let bigCard = this.bigCards[dragResult.bigCardIndex];
        // if dropped small card was not already removed (because of multiple clicks or drags during animation)
        // then finish changing the visible state of the Big Card.
        if( bigCard.caseIndices.includes( dragResult.caseIndex )) {
            this.Dyn.assert( this.container.state.plot === "bigCards" || this.container.hasPlot());
            this.hideCardUnderBigCard( dragResult.smallCardIndex, dragResult.bigCardIndex );
            this.addBigCardCaseView( dragResult.bigCardIndex, dragResult.caseIndex );
            this.Dyn.toolTip.hide(); // fix bug where tooltip persists on firefox after drop
        }
    }

    /** handle click on small card when in OrderBy or GroupBy plot.
     *  A big card is opened, at the bottom of the home area.  */
    clickOpenSmallCardInBigCard( smallCardIndex, caseIndex, bigCardIndex ) {
        let Dyn = this.Dyn,
            bigCard = this.bigCards[bigCardIndex],
            undoData = this.container.save();

        // remove previous cases from big card, if any, with animation
        while( bigCard.cardHasCases()) {
            this.removeAnimateCardsFromBigCard( bigCardIndex );
        }
        // add new case to big card, with animation
        let smallCard = this.container.home.getSmallCard( smallCardIndex ),
            smallCardBBox = smallCard.outline.getBBox(),
            bigCardBBox = bigCard.getBBox( Dyn ),
            transform = ElemCard.transformToCoverBigCard( smallCardBBox, bigCardBBox ),
            isIconOnly = this.container.options.isIconOnly;
        smallCard.setIsDragged( bigCardBBox.x, bigCardBBox.y );
        smallCard.showHideCard( isIconOnly, Dyn ); // show plot outline immediately
        bigCard.addCase( caseIndex );
        smallCard.animateCardTo( transform, this.container.slowAnimation(),
            null, () => {
                // at the end of the animation
                if( bigCard.hasCaseIndex( caseIndex )) {
                    this._updateOneCard( bigCard, bigCardIndex ); // update big card
                    this.hideCardUnderBigCard( smallCardIndex, bigCardIndex ); // hide small card
                } else {
                    // card was removed by a later click, return it to the plot
                    const hasPlot = this.container.hasPlot();
                    this.container.home.animateCardToHome( smallCardIndex, undefined, hasPlot );
                }
                this.container.undoer.saveUndoOfState( 'showCard', undoData, Dyn.loc( "Show Card" ) )
            } );
    }

    /** change a big card from big card case view to an outline, and return the small card home. */
    removeAnimateCardsFromBigCard( bigCardIndex, noUndo=true ) {
        let Dyn = this.Dyn,
            bigCard = this.bigCards[bigCardIndex];
        this.Dyn.assert( bigCard.cardHasCases());
        let removedCaseIndex = bigCard.removeCase();
        const hasPlot = this.container.hasPlot(),
            undoData = noUndo ? null : this.container.save();

        // animate small card home; show the case below, or card outline if no cases
        let smallCardIndex = this.container.home.caseIndexToSmallCardIndex( removedCaseIndex );
        if( smallCardIndex >= 0 ) { // make sure it was not filtered out
            // move small card to current big card position, then animate home (or to plot home if in Plot state)
            this.hideCardUnderBigCard( smallCardIndex, bigCardIndex );
            this.container.home.animateCardToHome( smallCardIndex, undefined, hasPlot );
        }
        this._updateOneCard( bigCard, bigCardIndex );
        if( ! hasPlot ) {
            this.container.setState( this.getBigCardState());
            if( undoData ) {
                this.container.undoer.saveUndoOfState( 'hideCard', undoData, Dyn.loc( "Hide Card" ) );
            }
        }
    }

    removeAnimateCardsFromAllBigCards() {
        this.bigCards.forEach((bigCard, bigCardIndex) => {
            while( bigCard.cardHasCases()) {
                this.removeAnimateCardsFromBigCard( bigCardIndex );
            }
        });
    }

    /** remove the small card from any big card that is holding it, e.g. if filtering was changed */
    removeHideCard( smallCard ) {
        this.bigCards.find( (bigCard, bigCardIndex) => {
            if( bigCard.hasCaseIndex( smallCard.caseIndex )) {
                this.removeAnimateCardsFromBigCard( bigCardIndex );
            }
        });
    }

    /** Reset: close all visible big cards, show outlines again. Caller should handle returning any small cards to home */
    resetBigCards() {
        this.arrangeBigCards( false );
        this.bigCards.forEach((bigCard, bigCardIndex) => {
            while( bigCard.cardHasCases() ) {
                bigCard.removeCase();
            }
            this._updateOneCard( bigCard, bigCardIndex );
        });
        this.container.updateNonPlotState();
    }

    /** restore big card state from state data returned by getBigCardState() */
    restoreBigCards( data ) {
        const hasPlot = this.container.hasPlot();
        this.bigCards.forEach((bigCard, bigCardIndex) => {
            while( bigCard.cardHasCases() ) {
                bigCard.removeCase();
            }
            let newCases = data?.cases?.[bigCardIndex] || [];
            newCases.forEach(( caseIndex /*,i*/ ) => {
                bigCard.addCase( caseIndex );
                let smallCardIndex = this.container.home.caseIndexToSmallCardIndex( caseIndex );
                if( smallCardIndex >= 0 ) { // make sure it was not filtered out
                    let smallCard = this.container.home.getSmallCard( smallCardIndex );
                    if( hasPlot && smallCard && smallCard.isPlotted ) {
                        // if plotted but in big card, first show the plot outline it should return to
                        let drag = smallCard.getPlotCoords();
                        smallCard.setIsDragged( drag.x, drag.y );
                        smallCard.showHideCard( this.container.options.isIconOnly, this.Dyn );
                    }
                    this.hideCardUnderBigCard( smallCardIndex, bigCardIndex );
                }
            });
            this._updateOneCard( bigCard, bigCardIndex );
        });
        this.container.updateNonPlotState();
    }

    hideCardUnderBigCard( smallCardIndex, bigCardIndex ) {
        let Dyn = this.Dyn,
            smallCard = this.container.home.getSmallCard( smallCardIndex );
        const bigCardBBox = this.bigCards[bigCardIndex]?.getBBox( Dyn );
        smallCard.hideCardUnderBigCard( bigCardBBox, Dyn );
    }

    /**
     * Close and hide the visible big cards,
     * without changing the location of the small cards dropped onto them,
     * in preparation for moving the cards to an OrderBy or GroupBy plot.
     */
    hideBigCardsAndOutline() {
        this.bigCards.forEach((bigCard, bigCardIndex) => {
            let casesRemoved = false;
            while( bigCard.cardHasCases() ) {
                bigCard.removeCase();
                casesRemoved = true;
            }
            if( ! (ElemPlayContainer.bigCardsOnLeft || bigCardIndex===0) ) {
                bigCard.outline.hide();
            } else if( casesRemoved ) {
                this._updateOneCard( bigCard, bigCardIndex );
            }
        });
    }

    /** card dropped and open in big card area? */
    isCardInBigCardArea( caseIndex ) {
        return Boolean(this.bigCards.find((bc)=>bc.hasCaseIndex(caseIndex)));
    }

    /** is the small card (being dragged) overlapping and droppable on a big card outline? */
    isCardOverlappingBigCardArea( smallCardBBox, caseIndex ) {
        let Dyn = this.Dyn,
            bbox,
            bigCardIndex = this.bigCards.findIndex((bc) => {
                bbox = bc.getBBox( Dyn );
                return bc.cardIsVisible( Dyn )
                    && (! bc.hasCaseIndex( caseIndex ))
                    && smallCardBBox.isOverlappingRect( bbox );
            });
        return (bigCardIndex >=0) ? { bigCardIndex:bigCardIndex, bbox:bbox }: null;
    }

    /** is the small card (being dragged), in the big card view? If not fully in the view, return
     * a new location so that it still draggable (e.g. not mostly outside the view and hard to drag again */
    getDragConstraintforBigCardView( cardBBox ) {
        if( this.bounds.isOverlappingRect( cardBBox )) {

        }
        return null;
    }

}


